import classNames from "classnames";
import { useGrowl } from "PFApp/use_growl";
import { LoadingDots } from "PFComponents/loading_dots";
import SimplePagination from "PFComponents/pagination/simple_pagination";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import ExternalLinkIcon from "PFIcons/external_link.svg";
import RightArrow from "PFIcons/right_arrow.svg";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { Link } from "react-router-dom";

import { getAuditRoles } from "./api";
import css from "./engagement.less";
import EngagementProgressBar from "./engagement_progress_bar";
import ExceptionsPill from "./exceptions_pill";
import { workflowStatesByTabs } from "./helpers";
import RolesList from "./roles_list";

const Engagement = ({ engagement, tab, columns, fetchAllEngagements, workflowAutomation, roleFilters }) => {
  const {
    roles_workflow_state_tally: {
      auto_filling,
      auto_shortlisting,
      booked,
      confirmed,
      exception,
      filled,
      pending,
      shortlisting
    },
    roles_count,
    coowned_roles_count
  } = engagement;
  const filledCount = (booked || 0) + (filled || 0) + (exception || 0) + (confirmed || 0);
  const filledRolesRatio = filledCount / roles_count;

  const growl = useGrowl();
  const { formatDate } = useDateFormatter();
  const { engagementId, roleId } = useParams();
  const history = useHistory();
  const innerRef = useRef();
  const { t } = useTranslation("auditPlanner");

  const [showContent, setShowContent] = useState(Number(engagementId) === engagement.id);
  const [roles, setRoles] = useState({ entries: [], loading: false, errors: false, meta: null });
  const [page, setPage] = useState(1);
  const [confirmModeEnabled, setConfirmModeEnabled] = useState(false);
  const [height, setHeight] = useState(0);
  const isRoleOpened = useMemo(() => !!roles.entries.find(({ id }) => Number(roleId) === id), [roleId]);

  const fetchRoles = () => {
    if (showContent) {
      setRoles({ ...roles, loading: true });
      getAuditRoles({
        filters: {
          ...roleFilters,
          fields: {
            ...roleFilters?.fields,
            workflow_state: workflowStatesByTabs[tab],
            parent_activity_id: engagement.id
          }
        },
        page,
        perPage: 25
      })
        .then(({ entries, meta }) => {
          setRoles({ entries, loading: false, errors: false, meta });
          setConfirmModeEnabled(false);
        })
        .catch(() => {
          setRoles({ entries: [], loading: false, errors: true, meta: null });
          setShowContent(false);
          setConfirmModeEnabled(false);
          growl({
            message: t("parts.errorFetchingRoles"),
            kind: "error"
          });
        });
    } else {
      setHeight(0);
    }
  };

  useEffect(() => {
    setHeight(innerRef.current?.clientHeight || 0);
  }, [roles.loading]);

  const toggleContent = useCallback(() => {
    history.push(!showContent ? `/audit_planner/${tab}/${engagement.id}` : `/audit_planner/${tab}`);

    setShowContent((show) => !show);
  });

  const totalPages = Math.ceil((roles.meta?.total || 0) / 25);

  useEffect(() => {
    if (showContent && !isRoleOpened) {
      fetchRoles();
    }
  }, [showContent, page, isRoleOpened, roleFilters]);

  useEffect(() => {
    setHeight((showContent && roles.entries.length && innerRef.current?.clientHeight) || 0);
  }, [innerRef.current?.clientHeight, confirmModeEnabled, showContent]);

  useEffect(() => {
    if (showContent && roles.entries.length) {
      fetchRoles();
    }
  }, [workflowAutomation]);

  useEffect(() => {
    if (showContent) {
      fetchRoles();
    }
  }, [engagement]);

  return (
    <div
      className={classNames(css.engagementContainer, {
        [css.disabled]: coowned_roles_count === 0 || roles_count === 0 || roles.loading
      })}
      data-scroll-key={engagement.id}
    >
      <div className={css.engagementHeader}>
        <RightArrow
          onClick={toggleContent}
          width={40}
          height={40}
          className={classNames(css.arrow, {
            [css.arrowOpen]: showContent
          })}
        />
        <span className={classNames(css.title, { [css.controlCentre]: tab === "control_centre" })}>
          <h3 style={{ whiteSpace: "nowrap" }}>
            <span>
              <span
                className={css.name}
                role="button"
                tabIndex="0"
                onKeyDown={toggleContent}
                onClick={toggleContent}
              >
                {engagement.name}
              </span>
              <Link to={`/activities/${engagement.id}`}>
                <ExternalLinkIcon height={18} width={18} className={css.icon} />
              </Link>
            </span>
            {tab === "exceptions" && <ExceptionsPill />}
            {tab === "control_centre" && roles_count > 0 && (
              <span className={css.percentage}>
                <span style={{ fontWeight: 100 }}>{`${filledCount}/${roles_count} `}</span>
                <span
                  style={{ width: 35, display: "inline-block", textAlign: "right", fontWeight: 700 }}
                >{`${Math.round(filledRolesRatio * 100)}%`}</span>
              </span>
            )}
          </h3>
          {tab === "control_centre" && roles_count > 0 && (
            <EngagementProgressBar
              statusTally={{
                auto_filling,
                auto_shortlisting,
                booked,
                confirmed,
                exception,
                filled,
                pending,
                shortlisting
              }}
              total={roles_count}
            />
          )}
        </span>
        <span className={css.date}>
          <span className={css.dateInner}>
            {engagement.duration &&
              t("parts.startingDate", { date: formatDate(engagement.duration.start_date) })}
          </span>
        </span>
        {roles.loading && <LoadingDots />}
      </div>
      <div className={css.content} style={{ height }}>
        <div className={css.innerContent} ref={innerRef}>
          {roles.entries.length > 0 ? (
            <>
              <RolesList
                items={roles.entries}
                tab={tab}
                confirmModeEnabled={confirmModeEnabled}
                setConfirmModeEnabled={setConfirmModeEnabled}
                columns={columns}
                engagement={engagement}
                fetchAllEngagements={fetchAllEngagements}
              />
              {totalPages && (
                <SimplePagination
                  currentPage={page}
                  handleClick={(number) => setPage(number)}
                  totalPages={totalPages}
                  scrollOnClick={false}
                />
              )}
            </>
          ) : roles.loading ? (
            <LoadingDots />
          ) : (
            <div className={css.message}>
              {tab === "control_centre" ? t("parts.nothingToDisplay") : t("parts.noNestingRolesPermission")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

Engagement.propTypes = {
  columns: RolesList.propTypes.columns,
  engagement: PropTypes.object.isRequired,
  tab: PropTypes.string.isRequired,
  fetchAllEngagements: PropTypes.func.isRequired,
  workflowAutomation: PropTypes.shape({
    auto_filling: PropTypes.number.isRequired,
    auto_shortlisting: PropTypes.number.isRequired,
    pending: PropTypes.number.isRequired
  }).isRequired,
  roleFilters: PropTypes.object
};

export default Engagement;
